import { useMemo } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import type { DispenserRow } from '../dashboard.api';

interface DispenserPerformancePanelProps {
  data: DispenserRow[] | undefined;
  loading: boolean;
}

interface GrupoLado {
  lado: string;
  monto: number;
  galones: number;
  transacciones: number;
  mangueras: DispenserRow[];
}

/**
 * Columnas con ancho fijo para los números y flexible para el nombre: así los
 * totales del lado quedan alineados con el detalle de sus mangueras y la tabla se
 * lee de un vistazo.
 */
const COLUMNAS = 'grid grid-cols-[minmax(0,1fr)_4.5rem_6.5rem_7.5rem] items-center gap-x-3';

/**
 * Rendimiento por **lado** y **manguera**.
 *
 * El API entrega los campos `dispensador` y `pistola` (nombres heredados del
 * panel anterior); en la operación de una estación de servicio se dice *lado* y
 * *manguera*, que es lo que se muestra.
 */
export function DispenserPerformancePanel({ data, loading }: DispenserPerformancePanelProps) {
  const grupos = useMemo<GrupoLado[]>(() => {
    const mapa = new Map<string, GrupoLado>();

    for (const fila of data ?? []) {
      const clave = String(fila.dispensador);
      const grupo = mapa.get(clave) ?? {
        lado: clave,
        monto: 0,
        galones: 0,
        transacciones: 0,
        mangueras: [],
      };

      grupo.monto += Number(fila.monto || 0);
      grupo.galones += Number(fila.galones || 0);
      grupo.transacciones += Number(fila.transacciones || 0);
      grupo.mangueras.push(fila);

      mapa.set(clave, grupo);
    }

    const lista = [...mapa.values()];

    for (const grupo of lista) {
      // De mayor a menor monto: el orden muestra el aporte de cada manguera.
      grupo.mangueras.sort((a, b) => Number(b.monto || 0) - Number(a.monto || 0));
    }

    return lista.sort((a, b) => b.monto - a.monto);
  }, [data]);

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-20 rounded-control" />
        ))}
      </div>
    );
  }

  if (grupos.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-ink-500">
        Sin despachos registrados en este rango.
      </p>
    );
  }

  const maxMonto = Math.max(...grupos.map((grupo) => grupo.monto), 1);

  return (
    <div className="flex h-full flex-col gap-3">
      <div
        className={cn(
          COLUMNAS,
          'border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400',
        )}
      >
        <span>Lado / manguera</span>
        <span className="text-right">Ops</span>
        <span className="text-right">Galones</span>
        <span className="text-right">Monto</span>
      </div>

      <ul className="space-y-5">
        {grupos.map((grupo) => (
          <li key={grupo.lado}>
            <div className={COLUMNAS}>
              <span className="truncate text-sm font-semibold text-ink-900">
                Lado {grupo.lado}
              </span>
              <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                {formatNumber(grupo.transacciones)}
              </span>
              <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                {formatNumber(grupo.galones)}
              </span>
              <span className="text-right font-mono text-sm font-semibold text-ink-900 tabular-nums">
                {formatCurrency(grupo.monto)}
              </span>
            </div>

            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-ink-100">
              <div
                style={{ width: `${(grupo.monto / maxMonto) * 100}%` }}
                className="h-full rounded-full bg-gradient-to-r from-brand-500 to-accent-400"
              />
            </div>

            <ul className="mt-2 space-y-1.5 border-l border-ink-100 pl-3">
              {grupo.mangueras.map((manguera) => (
                <li
                  key={`${manguera.dispensador}-${manguera.pistola}-${manguera.combustible}`}
                  className={COLUMNAS}
                >
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="shrink-0 text-xs text-ink-400">
                      Manguera {manguera.pistola}
                    </span>
                    <span className="truncate text-xs text-ink-700">{manguera.combustible}</span>
                  </span>
                  <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                    {formatNumber(manguera.transacciones)}
                  </span>
                  <span className="text-right font-mono text-xs text-ink-500 tabular-nums">
                    {formatNumber(manguera.galones)}
                  </span>
                  <span className="text-right font-mono text-xs text-ink-800 tabular-nums">
                    {formatCurrency(manguera.monto)}
                  </span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
